import { useQuery } from "@tanstack/react-query";
import { STATES } from "@/data/states";
import { ALL_GAMES, type Game } from "@/data/games";
import { getResultsByDate, getAvailableResultDates } from "@/data/results";
import { fetchLiveLottery, buildLiveMaps } from "@/lib/lottery-api.functions";
import { useLiveLottery } from "./use-query-hooks";
import { getStoredResultsByDate, listStoredResultDates } from "@/lib/lottery-results.functions";
import { getGameOverridesCached } from "@/lib/game-overrides-read.functions";

type GameOverrideRow = {
  slug: string;
  name: string | null;
  logo_url: string | null;
  draw_schedule: string | null;
  description: string | null;
  updated_at?: string | null;
};

async function fetchGameOverrides(): Promise<GameOverrideRow[]> {
  try {
    return (await getGameOverridesCached()) as GameOverrideRow[];
  } catch {
    return [];
  }
}

function getOverrideVersion(overrides: GameOverrideRow[] | undefined) {
  return (overrides ?? [])
    .map((o) => `${o.slug}:${o.updated_at ?? ""}:${o.name ?? ""}:${o.logo_url ?? ""}:${o.draw_schedule ?? ""}:${o.description ?? ""}`)
    .sort()
    .join("|");
}

function applyGameOverride(g: Game, override?: GameOverrideRow | null): Game {
  if (!override) return g;
  return {
    ...g,
    name: override.name ?? g.name,
    logo_url: override.logo_url ?? g.logo_url,
    draw_schedule: override.draw_schedule ?? g.draw_schedule,
    description: override.description ?? g.description,
  };
}

function attachGameStates(r: any, overrideMap: Map<string, GameOverrideRow>) {
  const g = ALL_GAMES.find((x) => x.id === r.game_id);
  if (!g) return r;
  const overridden = applyGameOverride(g, overrideMap.get(g.slug));
  const st = g.state_id ? STATES.find((s) => s.id === g.state_id) : null;
  return {
    ...r,
    games: {
      ...overridden,
      states: st ? { name: st.name, slug: st.slug, abbreviation: st.abbreviation } : null,
    },
  };
}

export function useResultsByDate(date: string | undefined) {
  const { data: live } = useLiveLottery();
  const { data: overrides } = useQuery({
    queryKey: ["game-overrides"],
    queryFn: fetchGameOverrides,
    staleTime: 60_000,
  });
  const overrideMap = new Map<string, GameOverrideRow>((overrides ?? []).map((o) => [o.slug, o]));
  const overrideVersion = getOverrideVersion(overrides);
  return useQuery({
    queryKey: ["results-by-date", date, live?.data.fetched_at ?? "static", overrideVersion],
    queryFn: async () => {
      if (!date) return [];
      const base = getResultsByDate(date);
      // Fetch any DB-stored overrides for this date
      const stored = await getStoredResultsByDate({ data: { date } }).catch(() => [] as any[]);
      const storedByGame = new Map<string, any>(stored.map((s: any) => [s.game_id, s]));
      // For any game whose live latest matches this date, override numbers
      const merged = base.map((r) => {
        const s = storedByGame.get(r.game_id);
        if (s) {
          return {
            ...r,
            id: s.id,
            winning_numbers: s.winning_numbers,
            bonus_numbers: s.bonus_numbers,
            multiplier: s.multiplier,
          };
        }
        const liveLatest = live?.maps.latestByGameId[r.game_id];
        if (liveLatest && liveLatest.draw_date === date) {
          return {
            ...r,
            winning_numbers: liveLatest.winning_numbers,
            bonus_numbers: liveLatest.bonus_numbers,
            multiplier: liveLatest.multiplier,
          };
        }
        return r;
      });
      // Add DB-only entries
      const existingIds = new Set(merged.map((r) => r.game_id));
      for (const s of stored) {
        if (existingIds.has(s.game_id)) continue;
        const g = ALL_GAMES.find((x) => x.id === s.game_id);
        if (!g) continue;
        merged.push({
          id: s.id,
          game_id: s.game_id,
          draw_date: date,
          winning_numbers: s.winning_numbers,
          bonus_numbers: s.bonus_numbers,
          multiplier: s.multiplier,
          games: g as any,
        });
        existingIds.add(s.game_id);
      }
      // Add live-only entries for this date that aren't in the static set
      if (live) {
        for (const [gameId, l] of Object.entries(live.maps.latestByGameId)) {
          if (l.draw_date === date && !existingIds.has(gameId)) {
            const g = ALL_GAMES.find((x) => x.id === gameId);
            if (!g) continue;
            merged.push({
              id: `r-${gameId}-${date}`,
              game_id: gameId,
              draw_date: date,
              winning_numbers: l.winning_numbers,
              bonus_numbers: l.bonus_numbers,
              multiplier: l.multiplier,
              games: g as any,
            });
            existingIds.add(gameId);
          }
        }
      }
      return merged.map((r) => attachGameStates(r, overrideMap));
    },
    enabled: !!date,
    // Today's page keeps polling so games drawn later in the day appear automatically.
    staleTime: 60_000,
    refetchInterval: date === new Date().toISOString().slice(0, 10) ? 60_000 : false,
    refetchOnWindowFocus: true,
  });
}

export function useAvailableResultDates(limit = 60) {
  const { data: live } = useLiveLottery();
  return useQuery({
    queryKey: ["available-result-dates", limit, live?.data.fetched_at ?? "static"],
    queryFn: async () => {
      const dates = new Set(getAvailableResultDates(limit));
      if (live) {
        for (const l of Object.values(live.maps.latestByGameId)) {
          dates.add(l.draw_date);
        }
      }
      return Array.from(dates).sort((a, b) => b.localeCompare(a)).slice(0, limit);
    },
  });
}

// Re-export so callers can import from one place
export { fetchLiveLottery, buildLiveMaps };